/**
 * ShiftReasonTable — reasons down, shifts across, with a reconciling total.
 *
 * The grain the report needs: for each shift, the reason counts ADD UP TO that
 * shift's Total Events. That is why the sentinel reasons are included rather
 * than filtered out the way the Caregiver Report filters them — the requirement
 * is that a reader never has to wonder whether the breakdown and the headline
 * count describe the same events.
 *
 * So the Total row is not decoration. It is the assertion, rendered: it must
 * equal the Total Events row in the metrics table above, and if it ever does not
 * the report is telling you something is wrong with itself.
 *
 * Transposed for the same reason as the metrics table — comparing one reason
 * across three shifts is the question ("why is Night finishing without a
 * reason?"), and that is a row, not three columns to hunt through.
 */

import { useMemo } from 'react';
import {
    DASH,
    formatCount,
    formatMinutes,
    shiftColor,
} from '../../../../utils/shiftFormat.js';

/* The sentinels, in the order they should read: real reasons first, then the
   automatic ones, then no-reason. Sorting them into the middle of the list by
   volume would put "Auto Cleared" above a genuine reason code and imply it is
   one. */
const SENTINEL_RANK = { '-1': 2, '-2': 2, 0: 1 };
const sentinelRank = (id) => SENTINEL_RANK[String(id)] || 0;

const ShiftReasonTable = ({ records = [], isCloud = true }) => {
    /* One row per distinct reason across all shifts, each carrying a cell per
       shift. Built here rather than asked of the backend because the backend
       returns the natural grain (shift x reason) and this is purely how it is
       laid out. */
    const { rows, shifts, totals } = useMemo(() => {
        const shiftNames = records.map((r) => r.shift_name);
        const byReason = new Map();

        records.forEach((rec) => {
            (rec.reasons || []).forEach((rs) => {
                const key = String(rs.reason_id);
                if (!byReason.has(key)) {
                    byReason.set(key, {
                        reason_id: rs.reason_id,
                        label: rs.reason_code_description || 'No Reason',
                        cells: {},
                        total: 0,
                    });
                }
                const row = byReason.get(key);
                row.cells[rec.shift_name] = {
                    count: rs.total_events || 0,
                    avgService: rs.avg_service_time,
                    /* A sentinel row legitimately has no service time — the proc
                       nulls it for -1/-2 by design. Flagged so the cell can say
                       "n/a" rather than showing a bare dash that reads as
                       missing data. */
                    serviceNotApplicable: rs.reason_id === -1 || rs.reason_id === -2,
                };
                row.total += rs.total_events || 0;
            });
        });

        const list = [...byReason.values()].sort((a, b) => {
            const ra = sentinelRank(a.reason_id);
            const rb = sentinelRank(b.reason_id);
            if (ra !== rb) return ra - rb;
            return b.total - a.total;
        });

        const perShift = {};
        records.forEach((rec) => {
            perShift[rec.shift_name] = {
                breakdown: (rec.reasons || []).reduce((acc, rs) => acc + (rs.total_events || 0), 0),
                events: rec.total_events || 0,
            };
        });

        return { rows: list, shifts: shiftNames, totals: perShift };
    }, [records]);

    if (!rows.length) {
        return (
            <div className="sr-card">
                <div className="sr-card__head">
                    <h3 className="sr-card__title">Reason codes</h3>
                </div>
                <p className="sr-empty">No reason codes were recorded in this period.</p>
            </div>
        );
    }

    /* If any shift's breakdown does not sum to its Total Events, say so instead
       of letting the reader discover it by adding the column up. This should be
       impossible — every event carries a reason id, coalesced to 0 in the proc —
       so it fires only if that guarantee breaks. */
    const mismatched = shifts.filter((s) => totals[s] && totals[s].breakdown !== totals[s].events);

    return (
        <div className="sr-card">
            <div className="sr-card__head">
                <div>
                    <h3 className="sr-card__title">Reason codes</h3>
                    <p className="sr-card__sub">
                        Includes automatically closed alerts, so each column totals to that
                        shift’s Total Events. Automatic closures have no service time by design.
                    </p>
                </div>
            </div>

            <div className="sr-table-wrap">
                <table className="dp-table sr-reasons">
                    {/* Two header rows, so every figure gets its own column and its
                        own label. One cell used to hold BOTH the count and the average
                        service time, 6px apart, under a header that named only the
                        shift: nothing said which number was which and at a glance the
                        two read as one value. Mirrors the grouped header
                        AreaShiftTable already uses on the main dashboard. */}
                    <thead className="dp-thead">
                        <tr>
                            <th className="dp-th sr-metrics__rowhead" scope="col" rowSpan={2}>Reason</th>
                            {shifts.map((s) => (
                                <th
                                    key={s}
                                    className="dp-th sr-reasons__grouphead"
                                    scope="colgroup"
                                    colSpan={2}
                                    style={{ borderTopColor: shiftColor(s) }}
                                >
                                    <span className="sr-shiftname">
                                        <span
                                            className="sr-swatch"
                                            style={{ background: shiftColor(s) }}
                                            aria-hidden="true"
                                        />
                                        {s}
                                    </span>
                                </th>
                            ))}
                        </tr>
                        <tr>
                            {shifts.flatMap((s) => [
                                <th
                                    key={s + "-c"}
                                    scope="col"
                                    className="dp-th sr-reasons__subhead sr-reasons__col--first"
                                >
                                    Events
                                </th>,
                                <th key={s + "-s"} scope="col" className="dp-th sr-reasons__subhead">
                                    Avg Service
                                </th>,
                            ])}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr
                                key={String(row.reason_id)}
                                className={sentinelRank(row.reason_id) ? 'sr-reasons__row--sentinel' : undefined}
                            >
                                <th className="dp-td sr-metrics__rowhead" scope="row">{row.label}</th>
                                {shifts.flatMap((s) => {
                                    const cell = row.cells[s];
                                    if (!cell) {
                                        return [
                                            <td
                                                key={s + "-c"}
                                                className="dp-td sr-metrics__cell sr-metrics__cell--muted sr-reasons__col--first"
                                            >{DASH}</td>,
                                            <td
                                                key={s + "-s"}
                                                className="dp-td sr-metrics__cell sr-metrics__cell--muted"
                                            >{DASH}</td>,
                                        ];
                                    }
                                    return [
                                        <td key={s + "-c"} className="dp-td sr-metrics__cell sr-reasons__col--first">
                                            <span className="sr-reasons__count">{formatCount(cell.count)}</span>
                                        </td>,
                                        <td key={s + "-s"} className="dp-td sr-metrics__cell sr-reasons__svc">
                                            {cell.serviceNotApplicable
                                                ? <span title="Automatically closed events have no service period to measure">n/a</span>
                                                : `${formatMinutes(cell.avgService)} min`}
                                        </td>,
                                    ];
                                })}
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="sr-reasons__total">
                            <th className="dp-td sr-metrics__rowhead" scope="row">Total</th>
                            {shifts.flatMap((s) => [
                                <td key={s + "-c"} className="dp-td sr-metrics__cell sr-reasons__col--first">
                                    <span className="sr-reasons__count">
                                        {formatCount(totals[s]?.breakdown || 0)}
                                    </span>
                                </td>,
                                /* Blank on purpose. The Total row exists to reconcile
                                   the COUNT against Total Events in the metrics table;
                                   an average of averages is not a figure anyone should
                                   read, and a dash here would overload the dash that
                                   already means "this reason did not occur in this
                                   shift". Per-shift Avg Service lives above. */
                                <td key={s + "-s"} className="dp-td sr-metrics__cell" />,
                            ])}
                        </tr>
                    </tfoot>
                </table>
            </div>

            {mismatched.length ? (
                <p className="sr-note sr-note--warn">
                    {`Reason counts for ${mismatched.join(', ')} do not add up to that shift’s `
                     + 'total events. Treat this breakdown as incomplete and report it.'}
                </p>
            ) : null}

            {!isCloud ? (
                /* On a non-cloud campus reason 0 means both "no reason recorded"
                   and "auto-closed", and no query can separate them — so the No
                   Reason row silently contains automatic closures. Stated here
                   rather than only in the plan. */
                <p className="sr-note">
                    On this platform an automatically closed alert records the same reason code
                    as one where no reason was entered, so the <strong>No Reason</strong> row
                    includes automatic closures and average service time reads higher than it
                    otherwise would.
                </p>
            ) : null}
        </div>
    );
};

export default ShiftReasonTable;
